/**
 * PieceDraftSection — inline draft body for PieceView.
 *
 * S-095 / FEAT-007 (Phase 4). Replaces the legacy modal-based
 * DraftEditor for inline piece editing. Two modes:
 *
 *   - Read mode (default): renders the latest draft via PieceBody.
 *     The "Edit" button flips into write mode.
 *   - Write mode: renders the TipTap-based PieceDraftEditor with
 *     debounced auto-save (AUTOSAVE_DELAY_MS after the last keystroke). PATCHes
 *     /drafts/:id with the HTML body. Surfaces a transient "Saved"
 *     pill on success, mirroring the VoiceProfileEditor pattern
 *     (Phase 5 polish, 2026-05-25).
 *
 * Lock semantics: when the piece is in a terminal state (published,
 * scheduled, rejected, failed) the editor mounts in read-only mode
 * and the Edit button is hidden. The PieceDraftEditor's `disabled`
 * prop disables both the surface and the toolbar.
 *
 * Dirty-state navigation guard: while unsaved edits exist, a
 * `beforeunload` listener prompts the browser before tab close /
 * navigation. The debounce keeps the dirty window short.
 *
 * BUG-109: the dirty/baseline rules live in lib/draft-save-state.ts. The
 * baseline is what was last sent, never the server's re-serialized copy;
 * comparing against that copy saved in a loop (piece 1788, draft 958).
 *
 * Note on draft versioning: this PATCHes the latest existing draft
 * in-place — it does NOT create a new version row. That's a
 * deliberate FEAT-007 model change. The pipeline still creates new
 * versions on each run; editor edits accumulate on the latest one.
 */

import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApiMutation } from "@/lib/api";
import { PieceBody } from "@/components/PieceBody";
import { PieceDraftEditor } from "@/components/PieceDraftEditor";
import { Icon } from "@/components/Icon";
import { useRole } from "@/lib/useRole";
import { AUTOSAVE_DELAY_MS, doneAction, draftSaveReducer, initialDraftSaveState, shouldAutosave } from "@/lib/draft-save-state";
import type { PieceDraft, PieceStatus } from "@/types/piece";

interface Props {
  pieceId: number;
  pieceTitle: string;
  status: PieceStatus;
  latestDraft: PieceDraft | null;
}

// Statuses where the piece body should be read-only. Published /
// scheduled output is locked; rejected / failed pieces are terminal.
const LOCKED_STATUSES: ReadonlySet<PieceStatus> = new Set<PieceStatus>([
  "published",
  "scheduled",
  "rejected",
  "failed",
]);

interface DraftResponse {
  id: number;
  content: string | null;
  updated_at?: string;
}

export function PieceDraftSection({
  pieceId,
  pieceTitle,
  status,
  latestDraft,
}: Props) {
  const qc = useQueryClient();
  // FEAT-108 / S-438 — a Reviewer cannot edit a draft (PATCH /drafts is
  // Editor-or-Admin), so the editor is never offered to them: locked, the Edit
  // button is not rendered at all rather than shown and refused.
  const { canEdit } = useRole();
  const locked = LOCKED_STATUSES.has(status) || !canEdit;
  const initialContent = latestDraft?.content ?? "";
  const draftId = latestDraft?.id ?? null;

  const [editing, setEditing] = useState(false);
  const [save, dispatch] = useReducer(draftSaveReducer, initialContent, initialDraftSaveState);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [recentlySaved, setRecentlySaved] = useState(false);
  const draftHtml = save.html;
  const isDirty = save.dirty;

  // Re-baseline when the underlying draft swaps (e.g., pipeline ran and
  // produced a new version, or workspace switched). BUG-109 — while editing,
  // a refetch of the same draft is the echo of our own save; the reducer
  // ignores it rather than adopting the server's re-serialized HTML.
  const previousDraftId = useRef(draftId);
  useEffect(() => {
    const sameDraft = previousDraftId.current === draftId;
    previousDraftId.current = draftId;
    dispatch({ type: "serverContentChanged", html: initialContent, sameDraft, editing });
    if (!sameDraft) {
      setSavedAt(null);
      setRecentlySaved(false);
    }
    // editing is read, not watched: entering edit mode is not a content change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, initialContent]);

  const mutation = useApiMutation<DraftResponse, { content: string }>(
    `/drafts/${draftId ?? 0}`,
    "PATCH",
    {
      onSuccess: () => {
        // BUG-109 — the baseline becomes what was sent (the reducer), not
        // data.content: the server's sanitized copy never matches the
        // editor's own serialization, which is what kept it saving.
        dispatch({ type: "saveSucceeded" });
        setSavedAt(new Date());
        setRecentlySaved(true);
        // Invalidate so the right-rail draft list and other consumers
        // observe the latest content.
        void qc.invalidateQueries({ queryKey: ["piece-drafts", pieceId] });
        void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
      },
      onError: () => dispatch({ type: "saveFailed" }),
    }
  );

  // S-198 — manual ("Write myself") pieces are created with no draft row, so
  // there's nothing to PATCH and the inline editor never appears. This creates
  // the first draft row (page_type "draft" so PieceView picks it as latest),
  // then flips into edit mode once the refetched draft propagates down.
  const createMutation = useApiMutation<
    DraftResponse,
    { page_type: string; title: string; content: string }
  >(`/pieces/${pieceId}/drafts`, "POST", {
    onSuccess: () => {
      setEditing(true);
      void qc.invalidateQueries({ queryKey: ["piece-drafts", pieceId] });
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
    },
  });

  // Debounced auto-save: AUTOSAVE_DELAY_MS after the last keystroke. Cancels on
  // subsequent edits (cleanup of the previous timer). Skips when not
  // dirty, when no draft exists yet (nothing to PATCH), or when the
  // piece is locked. BUG-109 review: nor after a failure until the text
  // changes — shouldAutosave — or it retried the failed save forever.
  useEffect(() => {
    if (!shouldAutosave(save) || !draftId || locked) return;
    const timer = window.setTimeout(() => {
      dispatch({ type: "saveStarted", sentHtml: draftHtml });
      mutation.mutate({ content: draftHtml });
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
    // mutation is stable via useApiMutation; intentionally omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftHtml, isDirty, save.saving, save.failedHtml, draftId, locked]);

  // BUG-109 — Done closes once everything is saved (at once, or after the
  // save it started or waited for). A failed save keeps it open.
  useEffect(() => {
    if (!save.readyToClose) return;
    setEditing(false);
    dispatch({ type: "closed" });
  }, [save.readyToClose]);

  // Auto-clear the "Saved" pill 2.5s after a successful save. Mirrors
  // VoiceProfileEditor (Phase 5 polish).
  useEffect(() => {
    if (!recentlySaved) return;
    const t = window.setTimeout(() => setRecentlySaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [recentlySaved]);

  // beforeunload guard while edits are unsaved.
  useEffect(() => {
    if (!isDirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const errorMessage = useMemo(() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      if (mutation.error.status === 403) {
        return "You don't have permission to edit this draft.";
      }
      if (mutation.error.status === 422) {
        return "Could not save — the server rejected the content.";
      }
      return mutation.error.message ?? "Could not save the draft.";
    }
    return (mutation.error as Error).message;
  }, [mutation.error]);

  // No draft yet. For locked pieces, keep the read-only empty state. Otherwise
  // (e.g. a manual "Write myself" piece) offer to create the first draft row —
  // the inline editor needs an existing draft to PATCH against. (S-198)
  if (!latestDraft || !draftId) {
    if (locked) {
      return <PieceBody markdown={null} />;
    }
    return (
      <div className="piece-draft-section">
        <div className="piece-draft-empty">
          <p className="piece-draft-empty-text">
            This piece has no draft yet.
          </p>
          <button
            type="button"
            className="article-edit-btn"
            disabled={createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                page_type: "draft",
                title: pieceTitle,
                content: "",
              })
            }
          >
            <Icon name="pen" size={14} />{" "}
            {createMutation.isPending ? "Starting…" : "Start writing"}
          </button>
          {createMutation.error && (
            <p className="piece-draft-empty-error">
              {createMutation.error instanceof ApiError
                ? (createMutation.error.message ?? "Could not create the draft.")
                : "Could not create the draft."}
            </p>
          )}
        </div>
      </div>
    );
  }

  const handleChange = (html: string) => {
    dispatch({ type: "changed", html });
  };

  const showEditor = editing && !locked;

  return (
    <div className="piece-draft-section">
      <div className="piece-draft-section-toolbar">
        {!locked && !editing && (
          <button
            type="button"
            className="article-edit-btn"
            onClick={() => setEditing(true)}
            title="Edit the draft body inline"
          >
            <Icon name="pen" size={14} /> Edit
          </button>
        )}
        {showEditor && (
          <>
            <button
              type="button"
              className="article-edit-btn"
              onClick={() => {
                // BUG-109 — no "Discard unsaved changes?": save first.
                const action = doneAction(save);
                dispatch({ type: "doneRequested" });
                if (action === "save-then-close") {
                  dispatch({ type: "saveStarted", sentHtml: draftHtml });
                  mutation.mutate({ content: draftHtml });
                }
              }}
              disabled={save.closeAfterSave}
              title="Save and stop editing"
            >
              {save.closeAfterSave ? "Saving…" : "Done"}
            </button>
            <PieceDraftSaveStatus
              isDirty={isDirty}
              isSaving={mutation.isPending}
              savedAt={savedAt}
              recentlySaved={recentlySaved}
              errorMessage={errorMessage}
            />
          </>
        )}
      </div>
      {showEditor ? (
        <PieceDraftEditor
          value={draftHtml}
          onChange={handleChange}
          // BUG-122 — never read-only during a save: that took the focus out
          // of the text after almost every word. Text typed while a save is
          // in flight stays dirty and is saved next (draft-save-state).
          disabled={locked}
        />
      ) : (
        <PieceBody markdown={latestDraft.content} stripTitle />
      )}
    </div>
  );
}

interface SaveStatusProps {
  isDirty: boolean;
  isSaving: boolean;
  savedAt: Date | null;
  recentlySaved: boolean;
  errorMessage: string | null;
}

function PieceDraftSaveStatus({
  isDirty,
  isSaving,
  savedAt,
  recentlySaved,
  errorMessage,
}: SaveStatusProps) {
  if (errorMessage) {
    return (
      <span
        className="piece-draft-save-status is-error"
        role="status"
        aria-live="polite"
      >
        {errorMessage}
      </span>
    );
  }
  if (isSaving) {
    return (
      <span className="piece-draft-save-status" role="status" aria-live="polite">
        Saving…
      </span>
    );
  }
  if (isDirty) {
    return (
      <span
        className="piece-draft-save-status is-dirty"
        role="status"
        aria-live="polite"
      >
        Unsaved changes
      </span>
    );
  }
  if (recentlySaved) {
    return (
      <span className="editor-saved-badge" role="status" aria-live="polite">
        Saved
      </span>
    );
  }
  if (savedAt) {
    return (
      <span className="piece-draft-save-status is-saved">
        Saved {savedAt.toLocaleTimeString()}
      </span>
    );
  }
  return null;
}
